/**
 * Ending a session.
 *
 * There are two doors into the archive and they hold a session in two different places,
 * so there are two ways to end one — but only one button, and it should not matter to
 * the person pressing it which kind of account they have.
 *
 * **Staff** arrive through Cloudflare Access, which holds the session in the
 * `CF_Authorization` cookie it set when the one-time PIN was accepted. The app has no
 * login of its own to undo, so signing out means asking Access to drop that cookie:
 * `/cdn-cgi/access/logout` is answered by the edge in front of this hostname and never
 * reaches the Worker. Asked for as a request, and followed by a navigation to the login
 * page — see `accessSignOut` in lib/access.js, which is where the reason lives.
 *
 * **Donors** arrive at the donor hostname, where the session is the Worker's own and
 * lives in a row it can delete.
 *
 * Two doors, and by now the same two steps at each: end the session, then go to `/`.
 *
 * Both end in a whole-page navigation rather than a re-render, and deliberately so —
 * leaving the document takes every record the query cache had in memory with it, which
 * is the point of the button. `replace` rather than `assign` so the page being left is
 * not one Back press away from being the current page again.
 */
import { accessSignOut } from './access'
import { donorSignOut } from './donor'
import { SIGN_IN, leaveFor } from './leaving'

/**
 * `reason` is what the screen left behind says while the navigation is in flight —
 * `signout` for the button, `idle` for the ninety-minute clock letting itself out. Either way
 * the app stops drawing the archive the moment this is called, which matters most on the
 * donor door: that one waits on a `DELETE` before it goes anywhere, and the pages it
 * leaves up in the meantime are the ones the session is being ended to put away.
 */
export function signOut(user, reason = 'signout') {
  // `SIGN_IN` at either door, both as the destination and as what the screen behind offers
  // as a link if the navigation below never lands. It is the same path on both hostnames
  // and means the right thing on each: the login page on the apex, the donor sign-in form
  // on the other. Notably *not* Cloudflare's logout URL, which is a dead end however you
  // arrive at it and an error page if you arrive twice.
  leaveFor(reason, SIGN_IN, () => (user?.isDonor ? donorSignOut() : accessSignOut()))
}

/**
 * Access is only ever in front of the deployed staff hostname; `wrangler dev` reaches
 * the `DEV_EMAIL` branch of authenticate() instead, and there is no cookie to clear.
 * Locally the way to be somebody else is to edit `DEV_EMAIL` in `.dev.vars`.
 *
 * A donor session is the Worker's own and can always be ended, locally included — the
 * dev identity is a staff-door arrangement and has nothing to do with that door.
 */
export const canSignOut = (user) => user?.isDonor === true || user?.identity !== 'dev'

export const NO_SIGN_OUT_HINT =
  'Cloudflare Access is not in front of the local dev server, so there is no session ' +
  'to end. Change DEV_EMAIL in .dev.vars to sign in as someone else.'

/** What the confirmation says the way back in will cost. */
export const SIGN_IN_AGAIN_HINT = (user) =>
  user?.isDonor
    ? 'You will need your email address and password to sign in again.'
    : 'Getting back in means another one-time PIN sent to that address.'

/**
 * Whether the warning about losing unsaved work belongs in the confirmation.
 *
 * Donors and viewers hold no write capability at all — there is no form on any page
 * they can reach that keeps something worth saving — so for them the line is a warning
 * about a thing that cannot happen, and the sort of sentence that makes a reader stop
 * and wonder what they have forgotten. Anyone who can create, update, or upload keeps
 * it, because for them a half-written update really does go with the session.
 */
export const mayLoseUnsavedWork = (user) =>
  Boolean(user?.caps?.create || user?.caps?.update || user?.caps?.uploadMedia)

/**
 * The ninety-minute clock letting itself out — see useIdleTimeout, which calls this and
 * nothing else does.
 *
 * The same two doors as the button, and the same navigation, because the end state is the
 * same: no session, and the login page rather than a readable archive. Only the words on
 * the way differ, and only because "signing you out after 90 minutes" answers a question
 * the person coming back to the window is about to ask.
 */
export const signOutForIdling = (user) => signOut(user, 'idle')
